import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import SharedTabButton from 'shared/components/buttons/tab-button/tab-button';
import PageHeader from 'shared/components/page-header/page-header';
import PageHeaderSkeleton from 'shared/components/page-header/page-header-skeleton';
import { useDocument } from 'core/hooks/useDocument';
import { VehicleChecksApiService } from 'core/api';
import dayjs from 'dayjs';
import {
  ICustomVehicleCheckField,
  IVehicleCheckDamageReport,
  IVehicleCheckDao,
} from 'core/api/types/vehicle-checks.interface';
import { VehicleChecksTab, vehicleChecksTabs } from 'core/constants/vehicle-checks-tab';
import SharedTable from 'shared/components/table/table';
import {
  ISharedTableColumn,
  ISharedTableCustomCellTemplate,
  ISharedTableRow,
} from 'shared/components/table/table-interface';
import SharedButton from 'shared/components/buttons/button';
import { useDialog } from 'core/providers/DialogProvider';
import ViewImageDialog from './dialogs/view-image-dialog';
import { useSharedData } from 'core/providers/SharedDataProvider';

const VehicleChecksQuestionsTableColumns: ISharedTableColumn[] = [
  { label: 'Check', key: 'label', templateId: 'text', width: 10 },
  { label: 'Value', key: 'value', templateId: 'text', width: 10 },
  { label: 'Note', key: 'note', templateId: 'text', width: 10 },
];

const DamageReportsTableColumns: ISharedTableColumn[] = [
  { label: 'Location', key: 'location', templateId: 'text', width: 10 },
  { label: 'Description', key: 'description', templateId: 'text', width: 10 },
  { label: '', key: 'action', templateId: 'action', width: 10 },
];

const VehicleChecksItem = () => {
  const { uid } = useParams();
  const dialog = useDialog();

  const { drivers, vehicles, settingsVehicleChecks } = useSharedData();

  const [selectedTab, setSelectedTab] = useState<VehicleChecksTab>(VehicleChecksTab.CHECKS);

  const { data: vehicleCheck, loading: vehicleCheckLoading } = useDocument<IVehicleCheckDao>({
    apiService: VehicleChecksApiService,
    uid,
    navigateTo: '/vehicle-checks',
  });

  const actionCellTemplate = (report: IVehicleCheckDamageReport) => (
    <div className='w-full flex justify-end space-x-2'>
      <SharedButton
        onClick={() => dialog?.openDialog(<ViewImageDialog imagePath={report.imagePath} />)}
        type='button'
        appearance='subtle'
        spacing='compact'
        label='View Image'
      />
    </div>
  );

  const vehicleChecksQuestionsTableRows = useMemo<ISharedTableRow<ICustomVehicleCheckField>[]>(() => {
    if (!vehicleCheck?.checks) {
      return [];
    }

    const checksRows = Object.keys(vehicleCheck.checks).map((key) => {
      const check = settingsVehicleChecks?.find((check) => check.uid === key);
      const value =
        check?.options?.find((option) => option.value === vehicleCheck.checks[key].value)?.label ??
        vehicleCheck.checks[key].value;
      return {
        key: key,
        data: {
          label: check?.name ?? 'Unknown',
          value,
          note: vehicleCheck.checks[key].note ?? '-',
        },
      };
    });

    const typeSetting = settingsVehicleChecks?.find((setting) => setting.name === 'Tyres');
    const typeValue =
      typeSetting?.options?.find((option) => option.value === vehicleCheck.tyres?.value)?.label ??
      vehicleCheck.tyres?.value;
    const tyresRow = vehicleCheck.tyres
      ? [
          {
            key: 'tyres',
            data: {
              label: 'Tyres',
              value: typeValue,
              note: vehicleCheck.tyres.note ?? '-',
            },
          },
        ]
      : [];
    return [...checksRows, ...tyresRow];
  }, [vehicleCheck?.checks, vehicleCheck?.tyres, settingsVehicleChecks]);

  const damageReportsTableRows = useMemo<ISharedTableRow<IVehicleCheckDamageReport>[]>(() => {
    if (!vehicleCheck?.damageReports) {
      return [];
    }

    return vehicleCheck.damageReports.map((report: IVehicleCheckDamageReport, index: number) => {
      return {
        key: index.toString(),
        data: report,
      };
    });
  }, [vehicleCheck?.damageReports]);

  const customTemplates: ISharedTableCustomCellTemplate[] = [{ template: actionCellTemplate, id: 'action' }];

  const getTabContent = (vehicleCheck: IVehicleCheckDao) => {
    switch (selectedTab) {
      case VehicleChecksTab.CHECKS:
        return (
          <SharedTable
            columns={VehicleChecksQuestionsTableColumns}
            rows={vehicleChecksQuestionsTableRows}
            isLoading={false}
          />
        );
      case VehicleChecksTab.DAMAGE_REPORTS:
        return (
          <SharedTable
            columns={DamageReportsTableColumns}
            rows={damageReportsTableRows}
            isLoading={false}
            customTemplates={customTemplates}
          />
        );
    }
  };

  const VehicleChecksItemDetails = ({ vehicleCheck }: { vehicleCheck: IVehicleCheckDao }) => {
    const vehicle = vehicles?.find((vehicle) => vehicle.uid === vehicleCheck.vehicleUid);

    const VehicleCheckDetails = [
      {
        label: 'Direction',
        value: vehicleCheck.direction === 'in' ? 'In' : 'Out',
        key: 'direction',
      },
      {
        label: 'Type',
        value: vehicleCheck.type === 'daily' ? 'Daily' : 'Weekly',
        key: 'type',
      },
      {
        label: 'Check Date',
        value: dayjs(vehicleCheck.checkDate.toDate()).format('HH:mm, DD/MM/YYYY'),
        key: 'checkDate',
      },
      {
        label: 'Driver',
        value: drivers?.find((driver) => driver.uid === vehicleCheck.driver.uid)?.fullName ?? 'Unknown',
        key: 'driver',
      },
      {
        label: 'Vehicle Registration',
        value: vehicle?.registration ?? 'Unknown',
        key: 'vehicleRegistration',
      },
      {
        label: 'Vehicle Make',
        value: vehicle?.make ?? 'Unknown',
        key: 'vehicleMake',
      },
      {
        label: 'Vehicle Model',
        value: vehicle?.model ?? 'Unknown',
        key: 'vehicleModel',
      },
      {
        label: 'Mileage',
        value: vehicleCheck.mileage,
        key: 'mileage',
      },
    ];

    return (
      <div className='flex-grow-0 flex-shrink-0 basis-[360px] bg-white shadow-md rounded-md'>
        <div className='border-b p-4 flex justify-between items-center'>
          <p className='headline-06'>Details</p>
        </div>
        <div className='p-4'>
          {VehicleCheckDetails.map((section) => (
            <div key={section.key} className='mt-4 first:mt-0'>
              <p className='body-02 text-gray-500'>{section.label}</p>
              <p className='body-01 break-words'>{section.value}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <>
        {!vehicleCheck || vehicleCheckLoading ? (
          <PageHeaderSkeleton actions={[]} subtitle={true} />
        ) : (
          <>
            <PageHeader
              title={`${vehicleCheck.name}`}
              subtitle={dayjs(vehicleCheck.checkDate.toDate()).format('HH:mm, DD/MM/YYYY')}
              showBack={true}
            />
            <div className='flex flex-row py-6'>
              <VehicleChecksItemDetails vehicleCheck={vehicleCheck} />
              <div className='pl-6 w-full'>
                <div className='relative'>
                  <div className='flex relative z-10'>
                    {vehicleChecksTabs.map((tab) => (
                      <SharedTabButton key={tab.key} changeTab={setSelectedTab} tab={tab} currentTab={selectedTab} />
                    ))}
                  </div>
                  <div className='absolute bottom-0 left-0 right-0 h-px bg-gray-200' />
                </div>
                <div className='mt-6'>{getTabContent(vehicleCheck)}</div>
              </div>
            </div>
          </>
        )}
      </>
    </>
  );
};

export default VehicleChecksItem;
